/**
 * Test específico para Firefox - diagnóstico detallado
 * Uso: await window.__testFirefox()
 */

export async function testFirefoxNotifications() {
  console.group('[Firefox] Diagnóstico detallado de notificaciones');
  
  // 1. Verificaciones básicas
  console.log('User Agent:', navigator.userAgent);
  console.log('Notification API:', typeof Notification);
  console.log('Permiso actual:', Notification?.permission);
  console.log('isSecureContext:', window.isSecureContext);
  console.log('Protocolo:', window.location.protocol);
  console.log('Hostname:', window.location.hostname);
  console.log('Puerto:', window.location.port);
  
  if (typeof Notification === 'undefined') {
    console.error('❌ Firefox no tiene API Notification disponible');
    console.groupEnd();
    return false;
  }
  
  // 2. Test de solicitud de permiso específico para Firefox
  if (Notification.permission === 'default') {
    console.log('🔔 Solicitando permiso en Firefox...');
    try {
      // Firefox soporta tanto Promise como callback, pero a veces la Promise falla
      let permission;
      
      // Intentar primero con Promise
      try {
        permission = await Notification.requestPermission();
        console.log('✅ Permiso obtenido via Promise:', permission);
      } catch (promiseErr) {
        console.warn('⚠️ Promise falló, intentando callback:', promiseErr);
        
        // Fallback a callback
        permission = await new Promise((resolve) => {
          Notification.requestPermission((result) => {
            resolve(result);
          });
        });
        console.log('✅ Permiso obtenido via callback:', permission);
      }
      
      if (permission !== 'granted') {
        console.error('❌ Permiso denegado:', permission);
        console.log('💡 En Firefox: clic en el escudo 🛡️ junto a la URL → Notificaciones → Permitir');
        console.groupEnd();
        return false;
      }
    } catch (err) {
      console.error('❌ Error solicitando permiso:', err);
      console.groupEnd();
      return false;
    }
  }
  
  if (Notification.permission !== 'granted') {
    console.error('❌ Sin permiso de notificaciones:', Notification.permission);
    console.groupEnd();
    return false;
  }
  
  // 3. Test de new Notification() básico
  console.log('\n🧪 Test 1: new Notification() básico');
  try {
    const n1 = new Notification('Test Firefox Básico', {
      body: 'Notificación simple sin opciones avanzadas',
      icon: '/favicon.ico'
    });
    
    n1.onclick = () => {
      console.log('✅ Click en notificación básica funcionó');
      n1.close();
    };
    
    n1.onerror = (err) => {
      console.error('❌ Error en notificación básica:', err);
    };
    
    n1.onshow = () => {
      console.log('✅ Notificación básica mostrada');
    };
    
    console.log('✅ new Notification() básico creado');
    
    // Cerrar después de 3 segundos
    setTimeout(() => n1.close(), 3000);
    
  } catch (err) {
    console.error('❌ Error creando notificación básica:', err);
  }
  
  // 4. Test con opciones avanzadas
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  console.log('\n🧪 Test 2: new Notification() con opciones avanzadas');
  try {
    const n2 = new Notification('Test Firefox Avanzado', {
      body: 'Notificación con tag, renotify y datos',
      icon: '/favicon.ico',
      tag: 'firefox-test-advanced',
      renotify: true,
      requireInteraction: false,
      silent: false,
      data: { url: '/' }
    });
    
    n2.onclick = () => {
      console.log('✅ Click en notificación avanzada funcionó');
      try {
        window.focus();
      } catch (_) {}
      n2.close();
    };
    
    n2.onerror = (err) => {
      console.error('❌ Error en notificación avanzada:', err);
    };
    
    n2.onshow = () => {
      console.log('✅ Notificación avanzada mostrada');
    };
    
    console.log('✅ new Notification() avanzado creado');
    
    // Cerrar después de 3 segundos
    setTimeout(() => n2.close(), 3000);
    
  } catch (err) {
    console.error('❌ Error creando notificación avanzada:', err);
  }
  
  // 5. Test de Service Worker (si está disponible)
  if ('serviceWorker' in navigator) {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log('\n🧪 Test 3: Service Worker showNotification');
    try {
      // Registrar SW si no está
      await navigator.serviceWorker.register('/sw-push.js');
      const reg = await navigator.serviceWorker.ready;
      
      console.log('✅ Service Worker listo:', reg.scope);
      console.log('SW activo:', !!reg.active);
      console.log('SW estado:', reg.active?.state);
      
      if (reg.active) {
        try {
          await reg.showNotification('Test Firefox SW', {
            body: 'Notificación via Service Worker en Firefox',
            icon: '/favicon.ico',
            tag: 'firefox-sw-test',
            data: { url: '/' }
          });
          console.log('✅ reg.showNotification() funcionó');
        } catch (swErr) {
          console.error('❌ reg.showNotification() falló:', swErr);
        }
      } else {
        console.warn('⚠️ Service Worker no está activo');
      }
      
    } catch (swErr) {
      console.error('❌ Error con Service Worker:', swErr);
    }
  } else {
    console.log('\n⚠️ Service Worker no disponible en este Firefox');
  }
  
  // 6. Configuración recomendada para Firefox
  console.log('\n🔧 Configuración recomendada para Firefox:');
  console.log('1. about:config → dom.webnotifications.enabled = true');
  console.log('2. about:config → dom.webnotifications.serviceworker.enabled = true');
  console.log('3. about:preferences#privacy → Notificaciones → Configuración');
  console.log('4. Verificar que no esté en modo privado (las notificaciones pueden estar limitadas)');
  
  console.groupEnd();
  return true;
}

/**
 * Test simplificado que solo usa new Notification() (sin SW)
 */
export async function testFirefoxSimple() {
  console.group('[Firefox] Test simplificado (solo new Notification)');
  
  if (typeof Notification === 'undefined') {
    console.error('❌ API Notification no disponible');
    console.groupEnd();
    return false;
  }
  
  if (Notification.permission !== 'granted') {
    console.log('🔔 Solicitando permiso...');
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.error('❌ Permiso denegado');
      console.groupEnd();
      return false;
    }
  }
  
  try {
    const n = new Notification('🦊 Firefox Simple', {
      body: 'Test básico para Firefox sin Service Worker',
      icon: '/favicon.ico'
    });
    
    n.onclick = () => {
      console.log('✅ Notificación clickeada');
      window.focus();
      n.close();
    };
    
    console.log('✅ Notificación enviada');
    setTimeout(() => n.close(), 5000);
    
    console.groupEnd();
    return true;
  } catch (err) {
    console.error('❌ Error:', err);
    console.groupEnd();
    return false;
  }
}